import { LitElement, html, css, nothing } from 'lit';
import { api, ApiError } from '../api.js';

/**
 * What the weekly digest sends you (PRD 7.7; TRD 10.6; Phase 36).
 *
 * The Settings page is a placeholder until a later phase fills it in, but this one control cannot
 * wait for it: a weekly email nobody can switch off is not a subscription, and the endpoint that
 * switches it off needs a session, which the /utils tools (Admin only) cannot give a Viewer. So
 * the two switches live here, on the page the digest's own footer links to.
 *
 * `include_individual` is an appetite, not a permission: turning it on does not make anyone
 * entitled to a named person's hours, and the server resolves the entitlement per recipient when
 * it builds the mail (TRD 5.2, 13). The copy says so, because a switch that looks like it grants
 * access and does not is worse than no switch.
 */
class DigestSubscription extends LitElement {
  static properties = {
    _subscription: { state: true },
    _loading: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _saved: { state: true },
  };

  constructor() {
    super();
    this._subscription = null;
    this._loading = false;
    this._saving = false;
    this._error = null;
    this._saved = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 22px;
      max-width: 720px;
    }
    h2 {
      margin: 0 0 6px;
      font-size: 18px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 16px;
      font-size: 13px;
      color: var(--ink-muted);
      max-width: 70ch;
    }
    label {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      margin: 0 0 12px;
      font-size: 14px;
      color: var(--ink-strong);
    }
    label input {
      margin-top: 3px;
    }
    label small {
      display: block;
      font-size: 12px;
      color: var(--ink-muted);
      font-weight: 400;
    }
    .note {
      font-size: 12px;
      color: var(--ink-muted);
      margin: 0;
    }
    .error {
      font-size: 13px;
      color: var(--status-over, #a3341f);
      margin: 0 0 8px;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  async _load() {
    this._loading = true;
    this._error = null;
    try {
      const payload = await api.get('/notifications/subscriptions');
      this._subscription = payload.subscription;
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : 'Could not read your subscription.';
    } finally {
      this._loading = false;
    }
  }

  /**
   * A checkbox here is not the state, it is a request to change it. The box is put back to what
   * the server last said before the write goes out, so a refused write cannot leave a switch
   * showing something that is not true — which matters more here than elsewhere, because one of
   * these switches looks like it controls who may see a named person's hours.
   *
   * @param {Event} event
   * @param {string} key
   */
  _toggle(event, key) {
    const wanted = event.target.checked;
    event.target.checked = this._subscription?.[key] ?? wanted;
    return this._set({ [key]: wanted });
  }

  async _set(patch) {
    this._saving = true;
    this._saved = false;
    this._error = null;
    try {
      const payload = await api.put('/notifications/subscriptions', patch);
      this._subscription = payload.subscription;
      this._saved = true;
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : 'Could not save that.';
    } finally {
      this._saving = false;
    }
  }

  render() {
    const s = this._subscription;
    return html`
      <div class="card">
        <h2>Weekly digest</h2>
        <p class="lede">
          A short note every Monday: the peak week and level, what is driving it, what changed since
          the last run, the decisions due, and one line on data hygiene. Every sentence links to the
          view it came from.
        </p>
        ${this._error ? html`<p class="error" role="alert">${this._error}</p>` : nothing}
        ${
          this._loading && !s
            ? html`<p class="note">Loading…</p>`
            : s
              ? html`
                  <label>
                    <input
                      type="checkbox"
                      .checked=${s.digest_enabled}
                      ?disabled=${this._saving}
                      @change=${(e) => this._toggle(e, 'digest_enabled')}
                    />
                    <span>
                      Send it to me
                      <small>${s.email}</small>
                    </span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      .checked=${s.include_individual}
                      ?disabled=${this._saving || !s.digest_enabled}
                      @change=${(e) => this._toggle(e, 'include_individual')}
                    />
                    <span>
                      Include the people I am responsible for
                      <small>
                        Only ever the people you can already see on the heatmap — yourself and, if
                        you manage anyone, your reports. Planners and Admins see everyone. Turning
                        this on grants nothing; turning it off keeps that section out of your mail.
                      </small>
                    </span>
                  </label>
                  <p class="note">${this._saving ? 'Saving…' : this._saved ? 'Saved.' : nothing}</p>
                `
              : nothing
        }
      </div>
    `;
  }
}

customElements.define('digest-subscription', DigestSubscription);
export { DigestSubscription };
